import { User } from "./User"

declare type SubscriptionStatus = "Active" | "Expired" | "HandledByOther" | "NotSubscribed";

export class Organization {
    id: string = ""
    name: string = ""
    isAdmin: boolean = false
    owners: User[] = []
    nxVetSubscriptionStatus: SubscriptionStatus = "NotSubscribed"
    nxCallSubscriptionStatus: SubscriptionStatus = "NotSubscribed"
    isNxVetSubscribed: boolean = false
    isNxCallSubscribed: boolean = false
    isStripeNxVetSubscribedCache: boolean = false
    isStripeNxCallSubscribedCache: boolean = false
    soapNoteRecipientEmail: string = ""
    nxVetForceSubscriptionEndDate: Date | undefined = undefined
    nxCallForceSubscriptionEndDate: Date | undefined = undefined
    isForceSubscription: boolean | undefined = undefined
    isShowingCompletePreferences: boolean = false

    phoneNumber: string = ""
    publicEmail: string = ""
    address: string = ""
    country: string = ""

    createdAt: Date | undefined = undefined
    updatedAt: Date | undefined = undefined
    createdBy: User | undefined = undefined
    updatedBy: User | undefined = undefined

    primaryLanguage: string = "";
    preferredMedicalRecordLanguage: string = "";
    patientManagementSoftware: string = "";
    otherPatientMangaugeSoftware: string = "";
    isEnablingOnMedicalRecordCompleteNotification: boolean = false;
    isRequiringRecordingConsent: boolean = false;
    reportIncludeCaseOwnerName: string | undefined = undefined;
    reportIncludeLogo: boolean = true;
    reportIncludeHospitalName: boolean = true;
    reportCustomHeaderText: string | null = null;
    reportIncludeHospitalInfo: boolean = true;
    reportIncludePhoneNumber: boolean = true;
    reportIncludeEmail: boolean = true;
    reportIncludeAddress: boolean = true;
    reportIncludePageNumber: boolean = true;
    reportIncludeDate: boolean = true;
    
    copyPasteFormat: string = "RichText";

    // When set, this org's medical record templates are managed by (read from)
    // the referenced organization and cannot be edited by this org's users.
    templateSourceOrganizationId?: string = undefined;
    templateSourceOrganizationName?: string = undefined;

    // Max duration (seconds) of a single NxHUB batch (manual) recording before
    // the device auto-stops. Admin-only; server clamps to 300–28800s. Default 3600.
    nxHubBatchMaxRecordingTimeS?: number = undefined;

    get isSubscribed(): boolean {
        return !!(this.isStripeNxVetSubscribedCache || this.isStripeNxCallSubscribedCache
            || (this.nxVetForceSubscriptionEndDate && this.nxVetForceSubscriptionEndDate > new Date())
            || (this.nxCallForceSubscriptionEndDate && this.nxCallForceSubscriptionEndDate > new Date()));
    }

    constructor(init?: Partial<Organization>) {
        if (!init) {
            return
        }

        Object.assign(this, init)

        if (init.owners) {
            this.owners = init.owners.map(o => new User(o))
        }

        if (init.soapNoteRecipientEmail) {
            this.soapNoteRecipientEmail = init.soapNoteRecipientEmail;
        }

        switch (this.nxVetSubscriptionStatus) {
            case "Active":
            case "Expired":
            case "HandledByOther":
                this.isNxVetSubscribed = true;
                break;
            default:
                this.isNxVetSubscribed = false;
                break;
        }

        switch (this.nxCallSubscriptionStatus) {
            case "Active":
            case "Expired":
            case "HandledByOther":
                this.isNxCallSubscribed = true;
                break;
            default:
                this.isNxCallSubscribed = false;
                break;
        }

        if (this.nxVetForceSubscriptionEndDate) {
            this.nxVetForceSubscriptionEndDate = new Date(this.nxVetForceSubscriptionEndDate);
        }

        if (this.nxCallForceSubscriptionEndDate) {
            this.nxCallForceSubscriptionEndDate = new Date(this.nxCallForceSubscriptionEndDate);
        }

        if (this.createdAt) {
            this.createdAt = new Date(this.createdAt);
        }

        if (this.updatedAt) {
            this.updatedAt = new Date(this.updatedAt);
        }
    }

    toString() {
        return this.name;
    }
}
